import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};

const SOURCE_STYLE = {
  invoice: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]',
  draft: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]',
  token: 'bg-[#CFFAFE] text-[#0E7490] border-[#A5F3FC]',
};

/**
 * ServiceLedgerDialog
 * -------------------
 * Renders every invoice / draft / token row that referenced the given service.
 * Clicking the voucher number opens the invoice (invoice rows only) in the
 * full-screen Edit Invoice page; on close the user returns here via the
 * `state.returnTo` plumbing on EditInvoicePage.
 */
export default function ServiceLedgerDialog({ service, open, onOpenChange }) {
  const [data, setData] = useState({ service_name: '', rows: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!open || !service?.id) return;
    setLoading(true);
    api.get(`/services/${service.id}/ledger`)
      .then(r => setData(r.data))
      .catch(() => setData({ service_name: service.name || '', rows: [] }))
      .finally(() => setLoading(false));
  }, [open, service]);

  const openInvoice = (row) => {
    if (row.source !== 'invoice') return; // drafts/tokens are not editable here
    onOpenChange(false);
    navigate(`/invoices/edit/${row.parent_id}`, { state: { returnTo: location.pathname } });
  };

  const totals = data.rows.reduce((acc, r) => {
    acc.qty += r.quantity || 0;
    acc.svc += (r.service_charge || 0) * (r.quantity || 1);
    acc.gov += (r.govt_fee || 0) * (r.quantity || 1);
    acc.total += r.total || 0;
    return acc;
  }, { qty: 0, svc: 0, gov: 0, total: 0 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="service-ledger-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Service Ledger</span>
            <span className="text-sm font-normal text-[#52525B]">— {data.service_name || service?.name}</span>
          </DialogTitle>
          <DialogDescription>
            Every invoice, draft and token where this service was used. Click an invoice number to open and edit it.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-6 text-sm text-[#52525B]">Loading…</div>
        ) : data.rows.length === 0 ? (
          <div className="p-6 text-sm text-[#A1A1AA] italic">No transactions recorded against this service yet.</div>
        ) : (
          <div className="overflow-auto flex-1">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Source</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Voucher</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Qty</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Svc Charge</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Govt Fee</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Work Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Payment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row, i) => (
                  <TableRow key={`${row.source}-${row.parent_id}-${row.item_index}-${i}`} className="border-[#F4F4F5]" data-testid={`service-ledger-row-${i}`}>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase font-semibold border ${SOURCE_STYLE[row.source] || ''}`}>{row.source}</Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {row.source === 'invoice' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-xs font-mono text-[#3B82F6] hover:text-[#2563EB] hover:bg-[#EFF6FF]"
                          onClick={() => openInvoice(row)}
                          data-testid={`service-ledger-open-${row.parent_id}`}
                        >
                          {row.voucher_number}
                        </Button>
                      ) : (
                        <span className="text-[#52525B]">{row.voucher_number}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(row.date)}</TableCell>
                    <TableCell className="text-xs font-medium">{row.customer_name || '—'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{row.quantity}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{fmt(row.service_charge * row.quantity)}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{fmt(row.govt_fee * row.quantity)}</TableCell>
                    <TableCell className="text-xs text-right font-mono font-semibold">{fmt(row.total)}</TableCell>
                    <TableCell className="text-xs capitalize text-[#52525B]">{row.work_status}</TableCell>
                    <TableCell className="text-xs capitalize text-[#52525B]">{row.payment_status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data.rows.length > 0 && (
          <div className="border-t border-[#E4E4E7] pt-3 mt-2 flex items-center justify-end gap-6 text-xs font-mono">
            <span className="text-[#52525B]">Rows: <strong className="text-[#0A0A0A]">{data.rows.length}</strong></span>
            <span className="text-[#52525B]">Qty: <strong className="text-[#0A0A0A]">{totals.qty}</strong></span>
            <span className="text-[#52525B]">Svc: <strong className="text-[#10B981]">{fmt(totals.svc)}</strong></span>
            <span className="text-[#52525B]">Govt: <strong className="text-[#3B82F6]">{fmt(totals.gov)}</strong></span>
            <span className="text-[#52525B]">Total: <strong className="text-[#0A0A0A]">{fmt(totals.total)}</strong></span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
